/*******************************************************************************
 * ADOBE CONFIDENTIAL
 * ___________________
 *
 * Copyright 2023 Adobe
 * All Rights Reserved.
 *
 * NOTICE: All information contained herein is, and remains
 * the property of Adobe and its suppliers, if any. The intellectual
 * and technical concepts contained herein are proprietary to Adobe
 * and its suppliers and are protected by all applicable intellectual
 * property laws, including trade secret and copyright laws.
 * Dissemination of this information or reproduction of this material
 * is strictly forbidden unless prior written permission is obtained
 * from Adobe.
 ******************************************************************************/

import styled from "styled-components";
import * as React from "react";
import { IconComponentProps, IconMode } from "components/Plans/IconComponent";

const ProductFeatureContainer = styled.li`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 6px;
  line-height: 20px;
  font-size: 16px;
`;

const Emphasis = styled.span`
  font-weight: 700;
  margin-right: 4px;
`;

export const ProductFeature: React.FC<{
  image: string | React.FC<IconComponentProps>;
  title?: string;
  description?: string;
  mode?: IconMode;
  compact?: boolean;
}> = ({
  image,
  children,
  mode = IconMode.light,
  title,
  description,
  compact = false,
}) => {
  const Image = image;
  return (
    <ProductFeatureContainer>
      {typeof image === "string" && (
        <ProductFeatureImage compact={compact} src={image} aria-hidden="true" />
      )}
      {typeof image === "function" && (
        <Image
          mode={mode}
          style={{ width: compact ? 18 : 24 }}
          aria-hidden="true"
        />
      )}
      <div style={{ display: "inline" }}>
        {title && <Emphasis>{title}</Emphasis>}
        <ProductFeatureDescription>{description}</ProductFeatureDescription>
        {children}
      </div>
    </ProductFeatureContainer>
  );
};

const ProductFeatureDescription = styled.span`
  font-weight: 400;
`;

const ProductFeatureImage = styled.img<{ compact: boolean }>`
  width: ${({ compact }): number => (compact ? 18 : 24)}px;
`;
